import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { ApiService } from '../../../shared/services/api.service';

export interface InstitutionProfile {
  iid: number;
  reportDate: Date;
  name: string;
  city: string;
  state: string;
  label: string;
  websiteAddress: string;
  bankType: string;
  assetGrowthYoy: number;
  assetGrowth3Yoy: number;
  annualAssetGrowth: number;
  totalAssetsAvg: number;
  totalAssetsQe: number;
  totalLiabilitiesAvg: number;
  totalLiabilitiesQe: number;
  totalLoanAvg: number;
  totalLoanQe: number;
  totalDepositsAvg: number;
  totalDepositQe: number;
  agiInd: number;
  commlInd: number;
  creInd: number;
  resiInd: number;
  consumerInd: number;
  hfsInd: number;
  lsefinSvcInd: number;
  ccsvcInd: number;
  wealthSvcInd: number;
  insuranceInd: number;
  resiallConc: number;
  commlConc: number;
  creConc: number;
  roeQe: number;
  roaQe: number;
  netIntMarginQe: number;
  texasRatioQe: number;
  nonIntIncToTotalAssetsQe: number;
  yeaQe: number;
  cofQe: number;
  efficiencyRatioQe: number;
  ownershipType?: string;
  lnsToDep: number;
  netOverhead: number;
  yldOnLnsQe: number;
  cstDepQe: number;
  isAdded: boolean;
  isRemoved: boolean;
}

export interface SimilarInstitutionsRequest {
  reportDate: string;
  states: string[];
  ownershipTypes: string[];
}

export interface TargetedInstitutionsRequest {
  reportDate: string;
  states: string[];
  ownershipTypes: string[];
  useAssets: boolean;
  assetsMin?: number;
  assetsMax?: number;
  useLnsToDep: boolean;
  lnsToDepMin?: number;
  lnsToDepMax?: number;
  useAssetGrowth: boolean;
  assetGrowthMin?: number;
  assetGrowthMax?: number;
  agiInd: boolean;
  commlInd: boolean;
  creInd: boolean;
  resiInd: boolean;
  consumerInd: boolean;
  hfsInd: boolean;
  ccsvcInd: boolean;
  lsefinSvcInd: boolean;
  wealthSvcInd: boolean;
  insuranceInd: boolean;
  roeFilter: number;
  roeMin?: number;
  roeMax?: number;
  roaFilter: number;
  roaMin?: number;
  roaMax?: number;
  netIntMarginFilter: number;
  netIntMarginMin?: number;
  netIntMarginMax?: number;
  netOverheadFilter: number;
  netOverheadMin?: number;
  netOverheadMax?: number;
  yeaFilter: number;
  yeaMin?: number;
  yeaMax?: number;
  yldOnLnsFilter: number;
  yldOnLnsMin?: number;
  yldOnLnsMax?: number;
  cofFilter: number;
  cofMin?: number;
  cofMax?: number;
  cstDepFilter: number;
  cstDepMin?: number;
  cstDepMax?: number;
  texasRatioFilter: number;
  texasRatioMin?: number;
  texasRatioMax?: number;
}

export interface PeerGroupSelection {
  selectionCriteria: string;
  institutionProfiles: InstitutionProfile[];
}

@Injectable({
  providedIn: 'root'
})
export class InstitutionProfileService {
  private apiService = inject(ApiService);

  constructor() { }

  public getInstitutionProfile(iid: number): Observable<InstitutionProfile> {
    return this.apiService.get<InstitutionProfile>(`/api/${iid}/profile`);
  }

  public getInstitutionProfiles(iid: number): Observable<InstitutionProfile[]> {
    return this.apiService.get<InstitutionProfile[]>(`/api/${iid}/profile/all`);
  }

  public getGroupInstitutionProfiles(iid: number, groupId: string): Observable<InstitutionProfile[]> {
    return this.apiService.get<InstitutionProfile[]>(`/api/${iid}/profile/${groupId}`);
  }

  public getSimilarInstitutions(iid: number, model: SimilarInstitutionsRequest): Observable<PeerGroupSelection> {
    return this.apiService.post<PeerGroupSelection>(`/api/${iid}/profile/ffiec/similar`, model);
  }

  public getTargetedInstitutions(iid: number, model: TargetedInstitutionsRequest): Observable<PeerGroupSelection> {
    return this.apiService.post<PeerGroupSelection>(`/api/${iid}/profile/ffiec/targeted`, model);
  }
}
